// @flow
import React from 'react';
import {
  DateTime,
  Interval,
} from 'luxon';
import Button, {
  ButtonGroup,
} from '@atlaskit/button';
import {
  DatePicker,
} from '@atlaskit/datetime-picker';

import type {
  IntervalType,
  DatePickerMode,
} from 'chronos-types';

import Check from 'chronos-assets/images/check-icon.svg';
import ChevronLeft from 'chronos-assets/images/chevron-left.svg';
import ChevronRight from 'chronos-assets/images/chevron-right.svg';

import {
  Flex,
} from 'chronos-styled';

import AtlassianDatePicker from '../AtlassianDatePicker';
import {
  getPopupDateLabel,
  getCurrentDate,
} from '../utils';

import {
  DatePickerJumptoControl,
} from '../styled';

import * as S from './styled';

const modes: Array<DatePickerMode> = ['Day', 'Week', 'Month', 'Custom'];

type Props = {
  date: IntervalType,
  mode: DatePickerMode,
  onChange: { (): void },
  showApplyButton: boolean,
  onApply: { (): void },
  onClickMode: { (): void },
  jumpToDateRequest: { (): void },
};

const PickerPopup = ({
  mode,
  date,
  onChange,
  onApply,
  showApplyButton,
  onClickMode,
  jumpToDateRequest,
}: Props) => (
  <S.PopupContent>
    <S.PopupInner>
      <S.PickerPopupModes>
        <ButtonGroup>
          {modes.map(item => (
            <Button
              key={item}
              onClick={() => onClickMode(item)}
              isSelected={item === mode}
            >
              {item}
            </Button>
          ))}
        </ButtonGroup>
      </S.PickerPopupModes>
      <Flex column>
        <S.PickerPopupContext>
          {mode !== 'Custom' && (
            <S.PickerPopupJumpto>
              <DatePickerJumptoControl
                src={ChevronLeft}
                onClick={() => jumpToDateRequest('Prev')}
              />
              <S.PickerPopupCurrent>
                {getPopupDateLabel(date, mode)}
              </S.PickerPopupCurrent>
              <DatePickerJumptoControl
                src={ChevronRight}
                onClick={() => jumpToDateRequest('Next')}
              />
            </S.PickerPopupJumpto>
          )}
          <Flex
            column
            justify="center"
            align="center"
          >
            <Flex
              fillSpace
              row
              justify="center"
            >
              {mode === 'Day' ? (
                <div style={{ width: 180 }}>
                  <DatePicker
                    value={date.start.toISO()}
                    onChange={
                      value => (
                        onChange(
                          Interval.fromDateTimes(
                            DateTime.fromISO(value),
                            DateTime.fromISO(value),
                          ),
                        )
                      )
                    }
                  />
                </div>
              ) : (
                <Flex
                  fillSpace
                  spacing="15"
                  align="center"
                  justify="center"
                >
                  <div style={{ width: 160 }}>
                    <AtlassianDatePicker
                      value={date.start.toISODate()}
                      onChange={
                        value => (
                          onChange(
                            Interval.fromDateTimes(
                              DateTime.fromISO(value),
                              DateTime.fromObject(date.end.toObject()),
                            ),
                          )
                        )
                      }
                    />
                  </div>
                  <S.Separator />
                  <div style={{ width: 160 }}>
                    <AtlassianDatePicker
                      value={date.end.toISODate()}
                      onChange={
                        value => (
                          onChange(
                            Interval.fromDateTimes(
                              DateTime.fromObject(date.start.toObject()),
                              DateTime.fromISO(value),
                            ),
                          )
                        )
                      }
                    />
                  </div>
                </Flex>
              )}
              {
                showApplyButton
                && (
                  <S.ApplyButton onClick={onApply}>
                    <S.CheckIcon src={Check} />
                  </S.ApplyButton>
                )
              }
            </Flex>
            {(mode === 'Week' || mode === 'Month') && (
              <S.PickerPopupButtons>
                <Button
                  appearance="link"
                  onClick={() => jumpToDateRequest('Prev')}
                >
                  {`prev ${mode.toLowerCase()}`}
                </Button>
                <Button
                  appearance="link"
                  onClick={() => onChange(getCurrentDate(mode), 'both')}
                >
                  {`current ${mode.toLowerCase()}`}
                </Button>
              </S.PickerPopupButtons>
            )}
          </Flex>
        </S.PickerPopupContext>
      </Flex>
    </S.PopupInner>
  </S.PopupContent>
);

export default PickerPopup;
